import type {
  ExpectedUploadHeaders,
  MultipartUploadPartModelInput,
  PartModel,
} from 'generated/types';

const FILE_CHUNK_SIZE = 10_000_000;

async function uploadFileWithProgress(
  file: File,
  parts: PartModel[],
  onProgress?: (loaded: number, total: number) => void,
  expectedHeaders?: ExpectedUploadHeaders | null
): Promise<MultipartUploadPartModelInput[]> {
  const totalSize = file.size;
  const partSize = Math.min(totalSize, FILE_CHUNK_SIZE);
  let overallUploaded = 0;

  const responseParts = await Promise.all(
    parts.map(({ part: partNumber, presigned_url: url }, index) => {
      const start = index * partSize;
      const end = Math.min(totalSize, start + partSize);
      const part = file.slice(start, end);

      return new Promise<MultipartUploadPartModelInput>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let lastLoaded = 0;

        xhr.open('PUT', url, true);
        if (expectedHeaders) {
          xhr.setRequestHeader('Content-Type', expectedHeaders.contentType);
          xhr.setRequestHeader(
            'Content-Disposition',
            expectedHeaders.contentDisposition
          );
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const delta = e.loaded - lastLoaded;
            lastLoaded = e.loaded;
            overallUploaded += delta;
            onProgress?.(overallUploaded, totalSize);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const eTag = xhr.getResponseHeader('ETag');
            resolve({
              ETag: eTag?.replaceAll('"', '') ?? '',
              PartNumber: partNumber,
            });
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = reject;
        xhr.send(part);
      });
    })
  );
  return responseParts;
}

export { uploadFileWithProgress };
